import { api } from './client';

export interface MileageMeta {
  date?: string;
  destination?: string;
  purposeOfTravel?: string;
  miles?: number;
  rate?: number;
  totalMileage?: number;
  tollParking?: number;
  subsistenceAmount?: number;
}

export interface ClaimLineItem {
  invoiceNo?: string;
  description: string;
  amount: number;
  quantity?: number;
  meta?: MileageMeta | null;
}

export interface LegalEntityOption {
  id: string;
  name: string;
  shortName?: string;
  registrationNumber?: string;
  isDefault?: boolean;
}

export interface ClaimItem {
  id: string;
  referenceNumber?: string;
  companyEntityId?: string | null;
  companyName?: string | null;
  claimType: string;
  claimTypeLabel?: string;
  title?: string;
  description?: string | null;
  amount: number;
  claimDate: string;
  expenseDate?: string | null;
  status: string;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  docsRequestedNote?: string | null;
  supportingFiles?: string[];
  bankPayableTo?: string | null;
  bankName?: string | null;
  bankAccountNo?: string | null;
  documentsAttached?: string[];
  additionalDetails?: string | null;
  purchasePurpose?: string | null;
  items?: ClaimLineItem[];
  createdAt: string;
  employee?: { id: string; fullName: string };
}

export interface EmployeeProfile {
  fullName: string;
  designation?: string | null;
  department?: string | null;
  staffId?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
}

export interface ClaimReference {
  source: string;
  companies: LegalEntityOption[];
  defaultCompanyEntityId?: string | null;
  employeeCompanyEntityId?: string | null;
  employeeCompanyEntity?: LegalEntityOption | null;
  companyLegalName: string;
  formTypes: Record<string, { label: string; description?: string }>;
  documentTypes: Record<string, string>;
  purchasePurposes: Record<string, string>;
  approvalChain: { role: string; label: string }[];
  subsistenceRates: { breakfast: number; lunch: number; dinner: number };
  mileageRates: { car: number; motor: number };
  defaultLimits: Record<string, number>;
  employeeProfile?: EmployeeProfile | null;
}

export function getClaimReference() {
  return api<{ data: ClaimReference }>('/claims/reference');
}

export function getClaimsStats() {
  return api<{
    data: {
      totalCount: number;
      pendingCount: number;
      approvedCount: number;
      rejectedCount: number;
      totalAmount: number;
      pendingAmount: number;
    };
  }>('/claims/stats');
}

export function createClaim(body: {
  companyEntityId?: string;
  claimType: string;
  title: string;
  description?: string;
  amount?: number;
  expenseDate?: string;
  bankPayableTo?: string;
  bankName?: string;
  bankAccountNo?: string;
  documentsAttached?: string[];
  additionalDetails?: string;
  purchasePurpose?: string;
  vehicleType?: string;
  plateNo?: string;
  clientName?: string;
  submit?: boolean;
  items?: ClaimLineItem[];
  supportingFiles?: string[];
}) {
  return api<{ message: string; data: ClaimItem }>('/claims', {
    method: 'POST',
    body: JSON.stringify({ ...body, currency: 'MYR' }),
  });
}

export function getClaims(params?: {
  status?: string;
  scope?: 'mine' | 'team';
  page?: number;
  per_page?: number;
}) {
  const sp = new URLSearchParams();
  if (params?.status) sp.set('status', params.status);
  if (params?.scope) sp.set('scope', params.scope);
  if (params?.page != null) sp.set('page', String(params.page));
  if (params?.per_page != null) sp.set('per_page', String(params.per_page));
  const q = sp.toString() ? `?${sp}` : '';
  return api<{ data: ClaimItem[] }>(`/claims${q}`);
}

export function submitClaim(id: string) {
  return api<{ message: string; data: { id: string; status: string } }>(`/claims/${id}/submit`, {
    method: 'POST',
  });
}

export interface ClaimResubmitFieldUpdates {
  title?: string;
  amount?: number;
  additionalDetails?: string;
  bankPayableTo?: string;
  bankName?: string;
  bankAccountNo?: string;
}

export function resubmitClaim(id: string, supportingFiles: string[], fields?: ClaimResubmitFieldUpdates) {
  return api<{ message: string; data: { id: string; status: string } }>(`/claims/${id}/resubmit`, {
    method: 'POST',
    body: JSON.stringify({ supportingFiles, ...fields }),
  });
}

export async function uploadScanFile(base64Image: string): Promise<{ path: string; url: string }> {
  const res = await api<{ path: string; url: string }>('/document-scan/upload', {
    method: 'POST',
    body: JSON.stringify({ image: base64Image }),
  });
  if (res.error) throw new Error(res.error);
  if (!res.data) throw new Error('Upload failed');
  return res.data;
}

export function approveClaim(id: string) {
  return api<{ message: string }>(`/claims/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ action: 'approve' }),
  });
}

export function rejectClaim(id: string, rejectionReason: string) {
  return api<{ message: string }>(`/claims/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejectionReason }),
  });
}

export { canApproveTeam as canApproveClaims } from '@/lib/roles';

export {
  CLAIM_STATUS_LABELS,
  CLAIM_STATUS_VARIANT,
} from '@hrmydeck/design-tokens/status';
